import React, { useRef, useState, useEffect } from "react";
import MDBox from "shared/ui/Box/MDBox";
import MDTypography from "shared/ui/Typography/MDTypography";
import Icon from "@mui/material/Icon";
import PropTypes from "prop-types";
import { alpha } from "@mui/material/styles";

export default function CategoryBar({ categories, activeCategories, toggleCategory }) {
  const scrollRef = useRef(null);
  const [showFade, setShowFade] = useState(true);
  const [scrollLeft, setScrollLeft] = useState(0);

  const updateFade = () => {
    const el = scrollRef.current;
    if (!el) return;

    setScrollLeft(el.scrollLeft);
    setShowFade(el.scrollLeft + el.clientWidth < el.scrollWidth - 10);
  };

  useEffect(() => {
    const t = setTimeout(() => updateFade(), 0);

    const el = scrollRef.current;
    if (!el) return;

    el.addEventListener("scroll", updateFade);
    window.addEventListener("resize", updateFade);

    return () => {
      clearTimeout(t);
      el.removeEventListener("scroll", updateFade);
      window.removeEventListener("resize", updateFade);
    };
  }, [categories]); // 👈🔥 IMPORTANTE

  // Scroll vertical → horizontal
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const onWheel = (e) => {
      if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
        e.preventDefault();
        el.scrollLeft += e.deltaY * 1.4;
      }
    };

    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  const scrollRight = () => (scrollRef.current.scrollLeft += 220);
  const scrollLeftFn = () => (scrollRef.current.scrollLeft -= 220);

  return (
    <MDBox sx={{ position: "relative", width: "100%" }}>
      {scrollLeft > 5 && (
        <MDBox
          onClick={scrollLeftFn}
          sx={{
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            width: "40px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: (theme) =>
              `linear-gradient(to right, ${theme.palette.grey[100]}, ${alpha(
                theme.palette.grey[100],
                0
              )})`,
            cursor: "pointer",
            zIndex: 3,
          }}
        >
          <Icon sx={{ fontSize: "22px", color: "text.secondary" }}>chevron_left</Icon>
        </MDBox>
      )}

      <MDBox
        ref={scrollRef}
        sx={{
          display: "flex",
          gap: "12px",
          overflowX: "auto",
          overflowY: "hidden",
          whiteSpace: "nowrap",
          padding: "4px 6px",
          scrollbarWidth: "none",
          scrollBehavior: "smooth",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        <MDBox
          key="all"
          onClick={() => toggleCategory(null)}
          sx={{
            padding: "7px 16px",
            borderRadius: "14px",
            cursor: "pointer",
            userSelect: "none",
            backgroundColor:
              activeCategories.length === 0
                ? (theme) => alpha(theme.palette.primary.main, 0.1)
                : "background.paper",
            border: "1px solid",
            borderColor: activeCategories.length === 0 ? "primary.main" : "grey.300",
            boxShadow: "none",
          }}
        >
          <MDTypography
            sx={{
              fontSize: "0.82rem",
              fontWeight: 500,
              color: activeCategories.length === 0 ? "primary.main" : "text.primary",
              whiteSpace: "nowrap",
            }}
          >
            Todo
          </MDTypography>
        </MDBox>

        {categories.map((cat) => {
          const isActive = activeCategories.includes(cat.id);

          return (
            <MDBox
              key={cat.id}
              onClick={() => toggleCategory(cat.id)}
              sx={{
                padding: "7px 16px",
                borderRadius: "14px",
                cursor: "pointer",
                transition: "all 0.15s ease",
                userSelect: "none",
                backgroundColor: isActive
                  ? (theme) => alpha(theme.palette.primary.main, 0.1)
                  : "background.paper",
                border: "1px solid",
                borderColor: isActive ? "primary.main" : "grey.300",
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: isActive
                    ? (theme) => alpha(theme.palette.primary.main, 0.15)
                    : "grey.100",
                  transform: "translateY(-1px)",
                },
                "&:active": {
                  transform: "scale(0.96)",
                },
              }}
            >
              <MDTypography
                sx={{
                  fontSize: "0.82rem",
                  fontWeight: 500,
                  color: isActive ? "primary.main" : "text.primary",
                  whiteSpace: "nowrap",
                }}
              >
                {cat.name}
              </MDTypography>
            </MDBox>
          );
        })}
      </MDBox>

      {showFade && (
        <>
          <MDBox
            sx={{
              position: "absolute",
              right: 0,
              top: 0,
              bottom: 0,
              width: "70px",
              pointerEvents: "none",
              background: (theme) =>
                `linear-gradient(to right, ${alpha(theme.palette.grey[100], 0)}, ${
                  theme.palette.grey[100]
                })`,
            }}
          />

          <MDBox
            onClick={scrollRight}
            sx={{
              position: "absolute",
              right: 0,
              top: 0,
              bottom: 0,
              width: "40px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              zIndex: 4,
            }}
          >
            <Icon sx={{ fontSize: "22px", color: "text.secondary" }}>chevron_right</Icon>
          </MDBox>
        </>
      )}
    </MDBox>
  );
}

CategoryBar.propTypes = {
  categories: PropTypes.array.isRequired,
  activeCategories: PropTypes.array.isRequired,
  toggleCategory: PropTypes.func.isRequired,
};
